package org.teddapp.project.db

import app.cash.sqldelight.Query
import app.cash.sqldelight.Transacter
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlCursor
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlPreparedStatement
import com.tedd.db.TeddDatabase
import kotlin.coroutines.resume
import kotlin.coroutines.suspendCoroutine
import kotlin.js.Promise
import kotlinx.browser.window
import kotlinx.coroutines.await

@JsModule("sql.js")
@JsNonModule
private external fun initSqlJs(config: dynamic = definedExternally): Promise<dynamic>

/**
 * La base du navigateur : SQLite compilé en WebAssembly (sql.js), tenu en mémoire et
 * recopié dans le stockage du navigateur (IndexedDB) après chaque écriture.
 *
 * Tout le reste de l'application lit et écrit la base de façon synchrone. sql.js l'est
 * aussi, une fois chargé : il suffit donc d'attendre [prepare] avant d'ouvrir quoi que
 * ce soit, et rien d'autre ne change.
 */
object WebDatabase {
    private const val STORE = "files"
    private const val KEY = "tedd.db"
    private const val SAVE_DELAY_MS = 600

    private var sql: dynamic = null
    private var saved: dynamic = null
    private var storage: dynamic = null
    private var open: dynamic = null
    private var timer: Int? = null

    /** Charge sql.js et relit la base enregistrée. À attendre avant de créer les dépôts. */
    suspend fun prepare() {
        val config = js("({})")
        config.locateFile = { file: String -> file }
        sql = initSqlJs(config).await()
        storage = try { openStorage() } catch (_: Throwable) { null }
        val target = storage
        saved = if (target != null) try { read(target) } catch (_: Throwable) { null } else null
        // Un onglet fermé juste après une écriture : on enregistre sans attendre le délai.
        window.addEventListener("pagehide", { saveNow() })
    }

    internal val isNew: Boolean get() = saved == null

    internal fun openDatabase(): dynamic {
        val module = sql ?: error("WebDatabase.prepare() doit être attendu avant d'ouvrir la base")
        val bytes = saved
        open = if (bytes != null) js("new module.Database(bytes)") else js("new module.Database()")
        return open
    }

    /** Programme l'enregistrement : plusieurs écritures d'affilée n'en font qu'un. */
    internal fun scheduleSave() {
        timer?.let { window.clearTimeout(it) }
        timer = window.setTimeout({ saveNow() }, SAVE_DELAY_MS)
    }

    private fun saveNow() {
        timer?.let { window.clearTimeout(it) }
        timer = null
        val db = open ?: return
        val target = storage ?: return
        runCatching {
            val bytes = db.export()
            target.transaction(STORE, "readwrite").objectStore(STORE).put(bytes, KEY)
        }.onFailure { console.warn("teddApp : la base n'a pas pu être enregistrée dans le navigateur", it) }
    }

    private suspend fun openStorage(): dynamic = suspendCoroutine { continuation ->
        val request = window.asDynamic().indexedDB.open("teddapp", 1)
        request.onupgradeneeded = { request.result.createObjectStore(STORE) }
        request.onsuccess = { continuation.resume(request.result) }
        request.onerror = { continuation.resume(null) }
    }

    private suspend fun read(target: dynamic): dynamic = suspendCoroutine { continuation ->
        val request = target.transaction(STORE, "readonly").objectStore(STORE).get(KEY)
        request.onsuccess = { continuation.resume(request.result ?: null) }
        request.onerror = { continuation.resume(null) }
    }
}

actual class DatabaseDriverFactory {
    actual fun createDriver(): SqlDriver {
        val isNew = WebDatabase.isNew
        val driver = SqlJsDriver(WebDatabase.openDatabase()) { WebDatabase.scheduleSave() }
        driver.execute(null, "PRAGMA foreign_keys = ON", 0)
        if (isNew) TeddDatabase.Schema.create(driver)
        return driver
    }
}

/** Pilote SQLDelight synchrone au-dessus de sql.js. */
private class SqlJsDriver(private val db: dynamic, private val onWrite: () -> Unit) : SqlDriver {
    private val listeners = mutableMapOf<String, MutableSet<Query.Listener>>()
    private var transaction: Transacter.Transaction? = null

    override fun <R> executeQuery(
        identifier: Int?,
        sql: String,
        mapper: (SqlCursor) -> QueryResult<R>,
        parameters: Int,
        binders: (SqlPreparedStatement.() -> Unit)?,
    ): QueryResult<R> {
        val statement = db.prepare(sql)
        try {
            statement.bind(bound(binders))
            return mapper(Cursor(statement))
        } finally {
            statement.free()
        }
    }

    override fun execute(identifier: Int?, sql: String, parameters: Int, binders: (SqlPreparedStatement.() -> Unit)?): QueryResult<Long> {
        db.run(sql, bound(binders))
        // Dans une transaction, l'enregistrement attend sa fin : on n'écrit jamais un état intermédiaire.
        if (transaction == null) onWrite()
        return QueryResult.Value(db.getRowsModified().unsafeCast<Number>().toLong())
    }

    private fun bound(binders: (SqlPreparedStatement.() -> Unit)?): Array<dynamic> =
        Statement().also { binders?.invoke(it) }.values.toTypedArray()

    override fun newTransaction(): QueryResult<Transacter.Transaction> {
        val enclosing = transaction
        val next = Transaction(enclosing)
        transaction = next
        if (enclosing == null) db.run("BEGIN TRANSACTION")
        return QueryResult.Value(next)
    }

    override fun currentTransaction(): Transacter.Transaction? = transaction

    private inner class Transaction(override val enclosingTransaction: Transacter.Transaction?) : Transacter.Transaction() {
        override fun endTransaction(successful: Boolean): QueryResult<Unit> {
            if (enclosingTransaction == null) {
                db.run(if (successful) "END TRANSACTION" else "ROLLBACK TRANSACTION")
                onWrite()
            }
            transaction = enclosingTransaction
            return QueryResult.Unit
        }
    }

    override fun addListener(vararg queryKeys: String, listener: Query.Listener) {
        queryKeys.forEach { listeners.getOrPut(it) { mutableSetOf() }.add(listener) }
    }

    override fun removeListener(vararg queryKeys: String, listener: Query.Listener) {
        queryKeys.forEach { listeners[it]?.remove(listener) }
    }

    override fun notifyListeners(vararg queryKeys: String) {
        queryKeys.flatMap { listeners[it].orEmpty() }.distinct().forEach(Query.Listener::queryResultsChanged)
    }

    override fun close() {
        db.close()
    }
}

/** Les paramètres d'une requête, dans la forme que sql.js attend : nombres, textes, octets ou null. */
private class Statement : SqlPreparedStatement {
    val values = mutableListOf<dynamic>()

    private fun set(index: Int, value: dynamic) {
        while (values.size <= index) values.add(null)
        values[index] = value
    }

    override fun bindBytes(index: Int, bytes: ByteArray?) = set(index, bytes?.let { source ->
        val array = js("new Uint8Array(source.length)")
        for (i in source.indices) array[i] = source[i].toInt() and 0xff
        array
    })

    // Un entier long de Kotlin n'est pas un nombre pour JavaScript : il passe en flottant,
    // exact jusqu'à 2^53, bien au-delà d'une date en millisecondes.
    override fun bindLong(index: Int, long: Long?) = set(index, long?.toDouble())
    override fun bindDouble(index: Int, double: Double?) = set(index, double)
    override fun bindString(index: Int, string: String?) = set(index, string)
    override fun bindBoolean(index: Int, boolean: Boolean?) = set(index, boolean?.let { if (it) 1.0 else 0.0 })
}

private class Cursor(private val statement: dynamic) : SqlCursor {
    private var row: dynamic = null

    override fun next(): QueryResult<Boolean> {
        val more = statement.step().unsafeCast<Boolean>()
        row = if (more) statement.get() else null
        return QueryResult.Value(more)
    }

    private fun at(index: Int): dynamic = row[index]

    override fun getString(index: Int): String? {
        val value = at(index)
        return if (value == null) null else value.toString() as String
    }

    private fun number(index: Int): Number? {
        val value = at(index)
        return if (value == null) null else value.unsafeCast<Number>()
    }

    override fun getLong(index: Int): Long? = number(index)?.toDouble()?.toLong()
    override fun getDouble(index: Int): Double? = number(index)?.toDouble()
    override fun getBoolean(index: Int): Boolean? = number(index)?.let { it.toDouble() != 0.0 }
    override fun getBytes(index: Int): ByteArray? {
        val source = at(index)
        if (source == null) return null
        return ByteArray(source.length.unsafeCast<Int>()) { i -> source[i].unsafeCast<Int>().toByte() }
    }
}
